import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "https://images.pexels.com/photos/9400976/pexels-photo-9400976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000";

const CATEGORIES = [
  { label: "Voyage de noces", slug: "noces", featured: true, seed: "Je prépare un voyage de noces." },
  { label: "Voyage en couple", slug: "couple", seed: "Je souhaite organiser un voyage en couple." },
  { label: "Voyage en famille", slug: "famille", seed: "Je souhaite organiser un voyage en famille." },
  { label: "Voyage entre amis", slug: "amis", seed: "Nous partons entre amis, en groupe." },
  { label: "Grands voyages", slug: "grands", seed: "Je rêve d'un grand voyage sur plusieurs destinations." },
  { label: "Voyage senior", slug: "senior", seed: "Je prépare un voyage confortable pour des voyageurs seniors." },
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-x-0 -top-[12%] h-[124%] will-change-transform">
        <div className="w-full h-full bg-cover bg-center kenburns" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-[#1A1B18]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-32 md:pt-40 text-white">
        <div className="max-w-3xl">
          <motion.p {...fade(0.05)} className="label-eyebrow text-white/90" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.55)" }}>Maison de voyage sur-mesure</motion.p>
          <motion.h1 {...fade(0.15)} className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6">
            Le voyage<br />
            <em className="not-italic text-[#E4DCCF]">comme une œuvre</em> sur-mesure.
          </motion.h1>
          <motion.p {...fade(0.28)} className="mt-7 text-base md:text-lg text-white/85 max-w-xl leading-relaxed">
            Parce que chaque voyage mérite d'être aussi unique que celui qui le vit.
            Nous composons couples, familles, lunes de miel et grands voyages — au meilleur rapport qualité-prix.
          </motion.p>

          <motion.div {...fade(0.4)} className="mt-9 flex flex-wrap gap-4">
            <a href="#devis" data-testid="hero-cta-primary"
              className="bg-[#FAF9F6] text-[#1A1B18] px-8 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#96836B] hover:text-white transition-colors">
              Créer mon voyage sur-mesure
            </a>
            <a href="#destinations" data-testid="hero-cta-secondary"
              className="border border-white/70 text-white px-8 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-[#1A1B18] transition-colors">
              Explorer les destinations
            </a>
          </motion.div>

          <motion.div {...fade(0.52)} className="mt-8 flex flex-wrap gap-2.5" data-testid="hero-categories">
            {CATEGORIES.map((c) => (
              <button key={c.slug} type="button" data-testid={`hero-category-${c.slug}`}
                onClick={() => window.dispatchEvent(new CustomEvent("dt:concierge-open", { detail: { seed: c.seed } }))}
                className={`text-[10px] md:text-[11px] uppercase tracking-[0.18em] px-3.5 py-2 rounded-full border transition-colors ${
                  c.featured
                    ? "border-[#E4DCCF] bg-[#E4DCCF]/15 text-[#E4DCCF] hover:bg-[#E4DCCF] hover:text-[#1A1B18]"
                    : "border-white/40 text-white/85 hover:border-white hover:text-white"
                }`}>
                {c.label}
              </button>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
